import { __ } from '@web-stories-wp/i18n';
import {
  BEVEL_MAX_SIZE,
  BEVEL_MAX_SOFTNESS,
} from '../../../../elements/text/utils/getBevelDepth';

// Provisional authoring defaults. Existing text has no depth until enabled.
export const LEGACY_DEFAULTS = {
  version: 1,
  mode: 'off',
  size: 2,
  lightDirection: 315,
  softness: 0.5,
  strength: 60,
};
export const OPTIONS = [
  { id: 'off', label: __('Off', 'web-stories') },
  { id: 'raised', label: __('Up', 'web-stories') },
  { id: 'recessed', label: __('Down', 'web-stories') },
];
export const GLOSS_CONTOUR_OPTIONS = [
  { value: 'linear', label: __('Linear', 'web-stories') },
  { value: 'rounded', label: __('Rounded', 'web-stories') },
  { value: 'ring', label: __('Ring', 'web-stories') },
];
export const LEGACY_FIELDS = [
  {
    key: 'size',
    label: __('Depth size', 'web-stories'),
    suffix: __('Size', 'web-stories'),
    max: 8,
    unit: 'px',
  },
  {
    key: 'lightDirection',
    label: __('Depth light direction', 'web-stories'),
    suffix: __('Light direction', 'web-stories'),
    max: 359,
    unit: '°',
  },
  {
    key: 'softness',
    label: __('Depth softness', 'web-stories'),
    suffix: __('Soften', 'web-stories'),
    max: 4,
    unit: 'px',
  },
  {
    key: 'strength',
    label: __('Depth strength', 'web-stories'),
    suffix: __('Strength', 'web-stories'),
    max: 100,
    unit: '%',
  },
];

// Preserve the conversion recipe used by the earlier popup for legacy layers.
const LEGACY_BEVEL_DEFAULTS = {
  version: 2,
  mode: 'off',
  style: 'inner-bevel',
  technique: 'chisel-hard',
  size: 2,
  depth: 100,
  softness: 0.5,
  lightDirection: 315,
  altitude: 45,
  highlight: { color: '#ffffff', opacity: 60, blendMode: 'color-dodge' },
  shadow: { color: '#000000', opacity: 60, blendMode: 'linear-burn' },
  glossContour: 'linear',
  contour: { enabled: true, profile: 'ring', range: 15 },
};

// New effects and explicit Reset use a subtle rounded treatment.
// These are Studio presets, not a Photoshop numeric-parity claim.
export const DEFAULTS = {
  ...LEGACY_BEVEL_DEFAULTS,
  technique: 'smooth',
  softness: 0,
  highlight: { color: '#ffffff', opacity: 50, blendMode: 'screen' },
  shadow: { color: '#000000', opacity: 50, blendMode: 'multiply' },
  contour: { enabled: false, profile: 'linear', range: 100 },
};
export const TECHNIQUE_OPTIONS = [
  { value: 'smooth', label: __('Smooth', 'web-stories') },
  { value: 'chisel-hard', label: __('Chisel Hard', 'web-stories') },
];

export const FIELDS = [
  {
    key: 'depth',
    label: __('Bevel depth', 'web-stories'),
    suffix: __('Depth', 'web-stories'),
    max: 1000,
    unit: '%',
    section: 'structure',
  },
  { ...LEGACY_FIELDS[0], max: BEVEL_MAX_SIZE, section: 'structure' },
  { ...LEGACY_FIELDS[2], max: BEVEL_MAX_SOFTNESS, section: 'structure' },
  { ...LEGACY_FIELDS[1], section: 'shading' },
  {
    key: 'altitude',
    label: __('Light altitude', 'web-stories'),
    suffix: __('Altitude', 'web-stories'),
    max: 90,
    unit: '°',
    section: 'shading',
  },
  {
    key: 'highlight.opacity',
    label: __('Highlight opacity', 'web-stories'),
    suffix: __('Opacity', 'web-stories'),
    max: 100,
    unit: '%',
    section: 'highlight',
  },
  {
    key: 'shadow.opacity',
    label: __('Shadow opacity', 'web-stories'),
    suffix: __('Opacity', 'web-stories'),
    max: 100,
    unit: '%',
    section: 'shadow',
  },
  {
    key: 'contour.range',
    label: __('Contour range', 'web-stories'),
    suffix: __('Range', 'web-stories'),
    min: 1,
    max: 100,
    unit: '%',
    section: 'contour',
  },
];

export function getSetting(settings, key) {
  return key.split('.').reduce((value, part) => value?.[part], settings);
}

export function patchSettings(settings, patch) {
  return Object.entries(patch).reduce((current, [key, value]) => {
    const [group, child] = key.split('.');
    return child
      ? { ...current, [group]: { ...current[group], [child]: value } }
      : { ...current, [group]: value };
  }, settings);
}

export function settingsFor(element) {
  const depth = element.textDepth;
  if (depth?.version === 2) return { ...depth };
  if (depth?.version !== 1) return { ...DEFAULTS };
  const settings = {
    ...LEGACY_DEFAULTS,
    mode: OPTIONS.some(({ id }) => id === depth.mode) ? depth.mode : 'off',
  };
  LEGACY_FIELDS.forEach(({ key, max }) => {
    if (typeof depth[key] === 'number' && Number.isFinite(depth[key])) {
      settings[key] = Math.max(0, Math.min(max, depth[key]));
    }
  });
  return settings;
}

// Display the expanded settings without changing the layer. The first actual
// edit upgrades a legacy layer; sidebar toggles retain its existing renderer.
export function bevelSettingsFor(element) {
  const existing = settingsFor(element);
  if (existing.version === 2) return existing;
  return {
    ...LEGACY_BEVEL_DEFAULTS,
    mode: existing.mode,
    size: existing.size,
    lightDirection: existing.lightDirection,
    softness: existing.softness,
    // Legacy strength controlled opacity, not bevel height.
    highlight: {
      ...LEGACY_BEVEL_DEFAULTS.highlight,
      opacity: existing.strength,
    },
    shadow: { ...LEGACY_BEVEL_DEFAULTS.shadow, opacity: existing.strength },
  };
}
